import useSWR from 'swr';
import { api } from '../lib/api';
import type { FolderMappingResponse } from '../types';

export function FolderMappingPage() {
  const { data, error, isLoading } = useSWR<FolderMappingResponse>(
    '/api/classifier-config/folder-mapping',
    () => api.getFolderMapping(),
    { refreshInterval: 0 }
  );

  if (isLoading) {
    return (
      <div className="max-w-[1600px] mx-auto">
        <div className="bg-gray-900 rounded-lg border border-gray-800 p-8">
          <div className="animate-pulse flex items-center justify-center">
            <span className="text-gray-500">Loading folder mappings...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-[1600px] mx-auto">
        <div className="bg-red-950/50 border border-red-900 rounded-lg p-4">
          <p className="text-red-400">Error loading folder mappings: {error.message}</p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { mappings, unmappedLabels, totalMappings, totalUnmapped } = data;

  // Group unmapped labels by classifier
  const unmappedByClassifier: Record<string, typeof unmappedLabels> = {};
  for (const item of unmappedLabels) {
    if (!unmappedByClassifier[item.classifierName]) {
      unmappedByClassifier[item.classifierName] = [];
    }
    unmappedByClassifier[item.classifierName].push(item);
  }

  return (
    <div className="max-w-[1600px] mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Folder Mapping</h1>
          <p className="text-gray-400 mt-1">
            How classifier labels map to iConect folders
          </p>
        </div>
        <div className="flex gap-4 text-sm text-gray-500">
          <span className="text-emerald-400">{totalMappings} mapped</span>
          <span className="text-gray-500">{totalUnmapped} unmapped</span>
        </div>
      </div>

      {/* Mapped Labels Table */}
      <div className="bg-gray-900 rounded-lg border border-gray-800 overflow-hidden">
        <div className="px-4 py-3 bg-gray-800/50 border-b border-gray-800">
          <h2 className="text-lg font-semibold text-white">Active Mappings</h2>
          <p className="text-sm text-gray-400">Labels that route documents to iConect folders</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-800 text-left">
                <th className="py-2 px-4 text-xs font-medium text-gray-500 uppercase">Classifier:Label</th>
                <th className="py-2 px-4 text-xs font-medium text-gray-500 uppercase">Display Name</th>
                <th className="py-2 px-4 text-xs font-medium text-gray-500 uppercase">Primary Folder</th>
                <th className="py-2 px-4 text-xs font-medium text-gray-500 uppercase">Parent Folder</th>
                <th className="py-2 px-4 text-xs font-medium text-gray-500 uppercase text-center">Staging ID</th>
                <th className="py-2 px-4 text-xs font-medium text-gray-500 uppercase text-center">Production ID</th>
              </tr>
            </thead>
            <tbody>
              {mappings.map(m => (
                <tr key={m.compositeKey} className="border-b border-gray-800 hover:bg-gray-800/50">
                  <td className="py-3 px-4">
                    <code className="text-sm font-mono text-blue-400">{m.compositeKey}</code>
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-300">{m.labelDisplayName}</td>
                  <td className="py-3 px-4">
                    <div>
                      <span className="text-sm text-emerald-400">{m.primaryFolder}</span>
                      <p className="text-xs text-gray-500">{m.staging.primaryName ?? m.production.primaryName}</p>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    {m.parentFolder ? (
                      <div>
                        <span className="text-sm text-gray-300">{m.parentFolder}</span>
                        <p className="text-xs text-gray-500">{m.staging.parentName ?? m.production.parentName}</p>
                      </div>
                    ) : (
                      <span className="text-gray-600">--</span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-center">
                    {m.staging.primaryId !== null ? (
                      <code className="text-sm font-mono text-gray-300">{m.staging.primaryId}</code>
                    ) : (
                      <span className="text-xs px-1.5 py-0.5 bg-yellow-900/50 text-yellow-400 rounded">
                        not created
                      </span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-center">
                    {m.production.primaryId !== null ? (
                      <code className="text-sm font-mono text-gray-300">{m.production.primaryId}</code>
                    ) : (
                      <span className="text-xs px-1.5 py-0.5 bg-yellow-900/50 text-yellow-400 rounded">
                        not created
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Unmapped Labels */}
      {totalUnmapped > 0 && (
        <div className="bg-gray-900 rounded-lg border border-gray-800 overflow-hidden">
          <div className="px-4 py-3 bg-gray-800/50 border-b border-gray-800">
            <h2 className="text-lg font-semibold text-white">Unmapped Labels</h2>
            <p className="text-sm text-gray-400">
              Labels produced by classifiers that do not route to any folder
            </p>
          </div>
          <div className="p-4 space-y-4">
            {Object.entries(unmappedByClassifier).map(([classifierName, labels]) => (
              <div key={classifierName}>
                <p className="text-sm font-medium text-gray-300 mb-2">{classifierName}</p>
                <div className="flex flex-wrap gap-1.5">
                  {labels.map(item => (
                    <span
                      key={`${item.classifierName}:${item.label}`}
                      className="text-xs px-2 py-1 bg-gray-800 text-gray-500 rounded font-mono"
                      title={item.labelDisplayName}
                    >
                      {item.label}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="text-xs text-gray-600 text-center">
        Last fetched: {new Date(data.timestamp).toLocaleString()}
      </div>
    </div>
  );
}
